import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ContactPopup from '../components/ContactPopup';
import ProcessSlider from '../components/ProcessSlider';
import PricingSection from '../components/PricingSection';
import SEO from '../components/SEO';
import ResponsiveCardSlider from '../components/ResponsiveCardSlider';

const ServicePage = () => {
  const { service } = useParams();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [service]);

  const serviceData = {
    'trademark-registration': {
      title: 'Register Your Trademark',
      subtitle: 'Protect Your Brand with Federal Trademark Registration',
      description: 'Secure nationwide protection for your brand name, logo, or slogan with comprehensive trademark registration services. Our expert team guides you through every step of the process.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      benefits: [
        'Nationwide exclusive rights to your trademark',
        'Legal presumption of ownership and validity',
        'Ability to use the ® symbol',
        'Enhanced protection against counterfeiters',
        'Basis for international registration'
      ],
      features: [
        { icon: Shield, title: 'Comprehensive Search', description: 'Thorough search to identify potential conflicts' },
        { icon: FileText, title: 'Expert Filing', description: 'Professional application preparation and submission' },
        { icon: CheckCircle, title: 'Monitoring Service', description: 'Ongoing protection and infringement monitoring' },
        { icon: AlertCircle, title: 'Office Action Support', description: 'Expert response to USPTO inquiries' }
      ]
    },
    'ttab-petition': {
      title: 'TTAB Petition',
      subtitle: 'Expert Representation Before the Trademark Trial and Appeal Board',
      description: 'Professional assistance with oppositions, cancellations, and appeals before the TTAB. Navigate complex trademark disputes with confidence.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Experienced TTAB representation',
        'Strategic case development and planning',
        'Comprehensive evidence gathering',
        'Expert brief preparation and filing',
        'Negotiation and settlement support'
      ],
      features: [
        { icon: Shield, title: 'Opposition Proceedings', description: 'Challenge conflicting trademark applications' },
        { icon: FileText, title: 'Cancellation Actions', description: 'Petition to cancel existing registrations' },
        { icon: CheckCircle, title: 'Appeal Services', description: 'Appeal examining attorney refusals' },
        { icon: AlertCircle, title: 'Settlement Negotiation', description: 'Resolve disputes through negotiation' }
      ]
    },
    'clearance-search': {
      title: 'Clearance Search Services',
      subtitle: 'Comprehensive Trademark Search Before You File',
      description: 'Reduce risk and increase success rates with thorough trademark clearance searches. Make informed decisions about your brand before investing in registration.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070',
      benefits: [
        'Identify potential conflicts early',
        'Avoid costly application rejections',
        'Make informed branding decisions',
        'Detailed professional search reports',
        'Expert analysis and recommendations'
      ],
      features: [
        { icon: Shield, title: 'Federal Database Search', description: 'Search USPTO registered and pending marks' },
        { icon: FileText, title: 'State Trademark Search', description: 'Review state-level registrations' },
        { icon: CheckCircle, title: 'Common Law Search', description: 'Identify unregistered trademark uses' },
        { icon: AlertCircle, title: 'Domain Name Search', description: 'Check domain availability and conflicts' }
      ]
    },
    'ownership-transfers': {
      title: 'Trademark Ownership Transfers',
      subtitle: 'Seamless Transfer of Trademark Rights',
      description: 'Professional handling of trademark assignments and ownership transfers. Ensure proper documentation and recording with the USPTO.',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2070',
      benefits: [
        'Proper documentation of ownership changes',
        'USPTO recording and filing',
        'Chain of title verification',
        'Protection of buyer and seller interests',
        'Compliance with legal requirements'
      ],
      features: [
        { icon: Shield, title: 'Assignment Preparation', description: 'Draft proper assignment documents' },
        { icon: FileText, title: 'USPTO Recording', description: 'File and record with USPTO' },
        { icon: CheckCircle, title: 'Due Diligence', description: 'Verify trademark status and rights' },
        { icon: AlertCircle, title: 'Legal Compliance', description: 'Ensure all legal requirements are met' }
      ]
    },
    'trademark-renewal': {
      title: 'Managed Trademark Renewal',
      subtitle: 'Never Miss a Trademark Renewal Deadline',
      description: 'Comprehensive renewal management services to keep your trademark registrations active. We monitor deadlines and handle all filing requirements.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Automatic deadline monitoring',
        'Section 8 and Section 15 filings',
        'Specimen and declaration preparation',
        'Renewal reminders and notifications',
        'Lifetime trademark protection'
      ],
      features: [
        { icon: Shield, title: 'Deadline Tracking', description: 'Never miss critical renewal dates' },
        { icon: FileText, title: 'Filing Preparation', description: 'Expert preparation of renewal documents' },
        { icon: CheckCircle, title: 'Specimen Review', description: 'Ensure proper use documentation' },
        { icon: AlertCircle, title: 'Status Updates', description: 'Regular status updates and confirmations' }
      ]
    },
    'statements-of-use': {
      title: 'Submission of Statements of Use',
      subtitle: 'Complete Your Intent-to-Use Application',
      description: 'Expert preparation and filing of Statements of Use to convert your intent-to-use application into a registered trademark.',
      image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=2070',
      benefits: [
        'Timely filing to meet USPTO deadlines',
        'Proper specimen preparation',
        'Declaration drafting and review',
        'Extension requests when needed',
        'Maximize approval chances'
      ],
      features: [
        { icon: Shield, title: 'Specimen Analysis', description: 'Ensure specimens meet USPTO requirements' },
        { icon: FileText, title: 'Declaration Preparation', description: 'Professional drafting of declarations' },
        { icon: CheckCircle, title: 'Extension Requests', description: 'File extensions when more time needed' },
        { icon: AlertCircle, title: 'Filing Management', description: 'Handle all USPTO communications' }
      ]
    },
    'office-actions': {
      title: 'Support & Response for Trademark Office Actions',
      subtitle: 'Expert Responses to USPTO Office Actions',
      description: 'Professional assistance with responding to trademark office actions. Overcome rejections and objections with strategic responses.',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2070',
      benefits: [
        'Expert analysis of office action issues',
        'Strategic response development',
        'Legal research and argument preparation',
        'Amendment drafting when needed',
        'Maximized approval likelihood'
      ],
      features: [
        { icon: Shield, title: 'Issue Analysis', description: 'Comprehensive review of examiner concerns' },
        { icon: FileText, title: 'Response Drafting', description: 'Professional legal arguments and evidence' },
        { icon: CheckCircle, title: 'Amendment Services', description: 'Strategic amendments to overcome issues' },
        { icon: AlertCircle, title: 'Follow-up Support', description: 'Handle additional USPTO communications' }
      ]
    },
    'infringement-monitoring': {
      title: 'Active Monitoring for Trademark Infringement',
      subtitle: 'Protect Your Brand from Unauthorized Use',
      description: 'Continuous monitoring services to detect potential trademark infringements and unauthorized uses of your mark.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070',
      benefits: [
        'Early detection of potential infringements',
        'Regular monitoring reports',
        'New application screening',
        'Online marketplace monitoring',
        'Proactive brand protection'
      ],
      features: [
        { icon: Shield, title: 'Continuous Monitoring', description: 'Ongoing surveillance of trademark databases' },
        { icon: FileText, title: 'Detailed Reports', description: 'Regular updates on potential conflicts' },
        { icon: CheckCircle, title: 'Opposition Support', description: 'File oppositions when needed' },
        { icon: AlertCircle, title: 'Enforcement Guidance', description: 'Recommendations for enforcement actions' }
      ]
    },
    'trademark-cancellations': {
      title: 'Support & Guidance for Trademark Cancellations',
      subtitle: 'Navigate Trademark Cancellation Proceedings',
      description: 'Expert guidance for both petitioning to cancel third-party marks and defending against cancellation actions.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Strategic cancellation petitions',
        'Defense against cancellation actions',
        'Evidence gathering and presentation',
        'Legal brief preparation',
        'Settlement negotiation support'
      ],
      features: [
        { icon: Shield, title: 'Petition Preparation', description: 'File cancellation petitions with TTAB' },
        { icon: FileText, title: 'Defense Strategy', description: 'Defend your trademark rights' },
        { icon: CheckCircle, title: 'Evidence Collection', description: 'Gather supporting documentation' },
        { icon: AlertCircle, title: 'Negotiation Support', description: 'Explore settlement opportunities' }
      ]
    },
    'trademark-disputes': {
      title: 'Legal Representation for Trademark Disputes',
      subtitle: 'Comprehensive Legal Support for Trademark Conflicts',
      description: 'Full-service legal representation for trademark disputes, including litigation support and conflict resolution.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Experienced legal representation',
        'Strategic dispute resolution',
        'Litigation support services',
        'Negotiation and mediation',
        'Comprehensive case management'
      ],
      features: [
        { icon: Shield, title: 'Legal Strategy', description: 'Develop comprehensive dispute strategies' },
        { icon: FileText, title: 'Litigation Support', description: 'Full support for court proceedings' },
        { icon: CheckCircle, title: 'Mediation Services', description: 'Alternative dispute resolution' },
        { icon: AlertCircle, title: 'Case Management', description: 'End-to-end case handling' }
      ]
    },
    'anti-counterfeiting': {
      title: 'Combatting Counterfeiting & Trademark Infringement',
      subtitle: 'Protect Your Brand from Counterfeit Products',
      description: 'Comprehensive anti-counterfeiting services to protect your brand from unauthorized reproductions and infringements.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070',
      benefits: [
        'Online marketplace monitoring',
        'Takedown notice preparation',
        'Customs recordation assistance',
        'Brand protection strategies',
        'Enforcement action coordination'
      ],
      features: [
        { icon: Shield, title: 'Online Monitoring', description: 'Track counterfeit listings online' },
        { icon: FileText, title: 'Takedown Services', description: 'File DMCA and platform takedowns' },
        { icon: CheckCircle, title: 'Customs Protection', description: 'Register with US Customs' },
        { icon: AlertCircle, title: 'Enforcement Support', description: 'Coordinate legal enforcement' }
      ]
    },
    'copyright-registration': {
      title: 'Register Your Copyright',
      subtitle: 'Protect Your Creative Works with Copyright Registration',
      description: 'Secure federal copyright protection for your original works including literary, artistic, musical, and software creations.',
      image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=2073',
      benefits: [
        'Public record of copyright ownership',
        'Legal evidence of ownership',
        'Ability to file infringement lawsuits',
        'Eligibility for statutory damages',
        'Protection for creative works'
      ],
      features: [
        { icon: Shield, title: 'Application Preparation', description: 'Expert copyright registration filing' },
        { icon: FileText, title: 'Work Classification', description: 'Proper categorization of your work' },
        { icon: CheckCircle, title: 'Deposit Submission', description: 'Handle deposit requirements' },
        { icon: AlertCircle, title: 'Registration Support', description: 'Navigate the registration process' }
      ]
    },
    'copyright-enforcement': {
      title: 'Copyright Enforcement Assistance',
      subtitle: 'Take Action Against Copyright Infringement',
      description: 'Professional support for enforcing your copyright rights, including cease and desist letters and takedown notices.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Cease and desist letter preparation',
        'DMCA takedown notices',
        'Infringement investigation',
        'Enforcement strategy development',
        'Legal action coordination'
      ],
      features: [
        { icon: Shield, title: 'Infringement Analysis', description: 'Evaluate copyright violations' },
        { icon: FileText, title: 'Notice Preparation', description: 'Draft cease and desist letters' },
        { icon: CheckCircle, title: 'DMCA Services', description: 'File digital takedown notices' },
        { icon: AlertCircle, title: 'Legal Support', description: 'Coordinate enforcement actions' }
      ]
    },
    'copyright-transfers': {
      title: 'Copyright Transfers & Licensing Support',
      subtitle: 'Manage Copyright Assignments and Licenses',
      description: 'Professional assistance with copyright transfers, assignments, and licensing agreements to protect your interests.',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2070',
      benefits: [
        'Copyright assignment preparation',
        'Licensing agreement drafting',
        'Rights verification and due diligence',
        'Copyright Office recordation',
        'Legal compliance assurance'
      ],
      features: [
        { icon: Shield, title: 'Assignment Documents', description: 'Prepare transfer documentation' },
        { icon: FileText, title: 'License Agreements', description: 'Draft licensing terms' },
        { icon: CheckCircle, title: 'Rights Verification', description: 'Confirm ownership rights' },
        { icon: AlertCircle, title: 'Office Recording', description: 'Record with Copyright Office' }
      ]
    },
    'copyright-legal': {
      title: 'Legal Representation for Copyright',
      subtitle: 'Comprehensive Copyright Legal Services',
      description: 'Full-service legal representation for copyright matters, including litigation support and dispute resolution.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Expert copyright litigation support',
        'Dispute resolution services',
        'Fair use analysis and defense',
        'Infringement case management',
        'Strategic legal counsel'
      ],
      features: [
        { icon: Shield, title: 'Litigation Support', description: 'Full court proceeding assistance' },
        { icon: FileText, title: 'Legal Strategy', description: 'Develop case strategies' },
        { icon: CheckCircle, title: 'Fair Use Defense', description: 'Fair use analysis and defense' },
        { icon: AlertCircle, title: 'Case Management', description: 'End-to-end case handling' }
      ]
    },
    'copyright-portfolio': {
      title: 'Copyright Portfolio Management',
      subtitle: 'Manage Your Copyright Portfolio Effectively',
      description: 'Comprehensive management services for copyright portfolios, including registration tracking and renewal management.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Centralized portfolio management',
        'Registration status tracking',
        'Renewal deadline monitoring',
        'Strategic portfolio planning',
        'Comprehensive reporting'
      ],
      features: [
        { icon: Shield, title: 'Portfolio Tracking', description: 'Monitor all copyright registrations' },
        { icon: FileText, title: 'Status Updates', description: 'Regular portfolio reports' },
        { icon: CheckCircle, title: 'Renewal Management', description: 'Track renewal requirements' },
        { icon: AlertCircle, title: 'Strategic Planning', description: 'Portfolio optimization advice' }
      ]
    },
    'dmca-compliance': {
      title: 'DMCA Compliance',
      subtitle: 'Digital Millennium Copyright Act Compliance Services',
      description: 'Expert guidance on DMCA compliance for websites, platforms, and service providers.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070',
      benefits: [
        'DMCA policy development',
        'Safe harbor compliance',
        'Takedown notice handling',
        'Counter-notice procedures',
        'Platform protection strategies'
      ],
      features: [
        { icon: Shield, title: 'Policy Development', description: 'Create DMCA compliant policies' },
        { icon: FileText, title: 'Notice Processing', description: 'Handle takedown requests' },
        { icon: CheckCircle, title: 'Safe Harbor Setup', description: 'Establish safe harbor protections' },
        { icon: AlertCircle, title: 'Compliance Audit', description: 'Review current compliance status' }
      ]
    },
    'patent-search': {
      title: 'Existing Patent Search',
      subtitle: 'Comprehensive Patent Prior Art Searches',
      description: 'Thorough patent searches to identify prior art and assess patentability of your invention before filing.',
      image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?q=80&w=2070',
      benefits: [
        'Identify existing patents and prior art',
        'Assess patentability of invention',
        'Avoid costly rejections',
        'Detailed search reports',
        'Professional patentability opinions'
      ],
      features: [
        { icon: Shield, title: 'Prior Art Search', description: 'Comprehensive patent database search' },
        { icon: FileText, title: 'Search Reports', description: 'Detailed analysis and findings' },
        { icon: CheckCircle, title: 'Patentability Opinion', description: 'Expert assessment of patent prospects' },
        { icon: AlertCircle, title: 'Strategy Guidance', description: 'Recommendations for next steps' }
      ]
    },
    'patent-office-actions': {
      title: 'Response to Patent Office Actions',
      subtitle: 'Expert Responses to USPTO Patent Examiner Rejections',
      description: 'Professional assistance with responding to patent office actions and overcoming examiner rejections.',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2070',
      benefits: [
        'Expert analysis of examiner rejections',
        'Strategic response development',
        'Amendment and argument preparation',
        'Claim modification strategies',
        'Maximized approval chances'
      ],
      features: [
        { icon: Shield, title: 'Rejection Analysis', description: 'Comprehensive review of office action' },
        { icon: FileText, title: 'Response Preparation', description: 'Professional arguments and amendments' },
        { icon: CheckCircle, title: 'Claim Amendments', description: 'Strategic claim modifications' },
        { icon: AlertCircle, title: 'Follow-up Support', description: 'Handle subsequent actions' }
      ]
    },
    'patent-application': {
      title: 'Patent Application Submission',
      subtitle: 'Professional Patent Application Preparation and Filing',
      description: 'Expert preparation and filing of utility, design, and provisional patent applications with the USPTO.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070',
      benefits: [
        'Professional application drafting',
        'Technical specification preparation',
        'Claims drafting and optimization',
        'Drawing and figure preparation',
        'USPTO filing and tracking'
      ],
      features: [
        { icon: Shield, title: 'Application Drafting', description: 'Comprehensive patent application preparation' },
        { icon: FileText, title: 'Claims Development', description: 'Strategic claim drafting' },
        { icon: CheckCircle, title: 'Technical Drawings', description: 'Professional figure preparation' },
        { icon: AlertCircle, title: 'Filing Management', description: 'Handle USPTO submission' }
      ]
    },
    'patent-legal': {
      title: 'Legal Representation for Patent Protection',
      subtitle: 'Comprehensive Patent Legal Services',
      description: 'Full-service legal representation for patent matters, including prosecution, enforcement, and litigation support.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070',
      benefits: [
        'Expert patent prosecution',
        'Infringement analysis and litigation',
        'Patent portfolio strategy',
        'Licensing and monetization',
        'Comprehensive legal counsel'
      ],
      features: [
        { icon: Shield, title: 'Patent Prosecution', description: 'Full prosecution support' },
        { icon: FileText, title: 'Litigation Support', description: 'Infringement case assistance' },
        { icon: CheckCircle, title: 'Portfolio Management', description: 'Strategic patent planning' },
        { icon: AlertCircle, title: 'Licensing Support', description: 'Patent monetization guidance' }
      ]
    },
    'llc-formation': {
      title: 'LLC',
      subtitle: 'Limited Liability Company Formation Services',
      description: 'Professional LLC formation services to establish your business with limited liability protection and flexible management structure.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070',
      benefits: [
        'Limited liability protection',
        'Flexible management structure',
        'Pass-through taxation benefits',
        'Professional filing and documentation',
        'Ongoing compliance support'
      ],
      features: [
        { icon: Shield, title: 'Entity Formation', description: 'Complete LLC formation services' },
        { icon: FileText, title: 'Document Preparation', description: 'Articles of organization and operating agreement' },
        { icon: CheckCircle, title: 'State Filing', description: 'Handle all state filings' },
        { icon: AlertCircle, title: 'EIN Obtainment', description: 'Obtain federal tax ID number' }
      ]
    },
    'c-corporation': {
      title: 'C Corporation',
      subtitle: 'C Corporation Formation and Setup',
      description: 'Establish your C Corporation with professional incorporation services for businesses seeking traditional corporate structure.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070',
      benefits: [
        'Unlimited shareholders allowed',
        'Easier access to capital funding',
        'Perpetual existence',
        'Professional credibility',
        'Comprehensive formation support'
      ],
      features: [
        { icon: Shield, title: 'Incorporation Services', description: 'Complete C Corp formation' },
        { icon: FileText, title: 'Corporate Documents', description: 'Bylaws and organizational documents' },
        { icon: CheckCircle, title: 'State Registration', description: 'File with state authorities' },
        { icon: AlertCircle, title: 'Compliance Setup', description: 'Establish corporate compliance framework' }
      ]
    },
    's-corporation': {
      title: 'S Corporation',
      subtitle: 'S Corporation Election and Formation',
      description: 'Form your S Corporation to enjoy pass-through taxation while maintaining corporate structure and liability protection.',
      image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?q=80&w=2073',
      benefits: [
        'Pass-through taxation benefits',
        'Limited liability protection',
        'Corporate structure advantages',
        'Tax savings opportunities',
        'Professional formation services'
      ],
      features: [
        { icon: Shield, title: 'S Corp Formation', description: 'Complete formation and election' },
        { icon: FileText, title: 'IRS Election', description: 'File Form 2553 with IRS' },
        { icon: CheckCircle, title: 'Corporate Setup', description: 'Establish corporate structure' },
        { icon: AlertCircle, title: 'Compliance Planning', description: 'Ongoing compliance guidance' }
      ]
    },
    'non-profit': {
      title: 'Non-Profit',
      subtitle: 'Non-Profit Organization Formation',
      description: 'Establish your non-profit organization with 501(c)(3) tax-exempt status application and comprehensive formation services.',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=2073',
      benefits: [
        'Tax-exempt status qualification',
        '501(c)(3) application support',
        'Donor contribution deductibility',
        'Grant eligibility',
        'Complete formation assistance'
      ],
      features: [
        { icon: Shield, title: 'Non-Profit Formation', description: 'Complete entity formation' },
        { icon: FileText, title: '501(c)(3) Application', description: 'IRS tax-exempt status filing' },
        { icon: CheckCircle, title: 'Bylaws & Governance', description: 'Establish governance structure' },
        { icon: AlertCircle, title: 'Compliance Setup', description: 'Non-profit compliance framework' }
      ]
    },
    'partnership-consultation': {
      title: 'Legal Consultation on Partnerships & LLPs',
      subtitle: 'Partnership Structure and LLP Formation',
      description: 'Expert consultation and formation services for partnerships and limited liability partnerships.',
      image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?q=80&w=2070',
      benefits: [
        'Partnership agreement drafting',
        'LLP formation services',
        'Legal structure consultation',
        'Partner rights and obligations',
        'Dispute resolution planning'
      ],
      features: [
        { icon: Shield, title: 'Partnership Formation', description: 'Establish partnership entities' },
        { icon: FileText, title: 'Agreement Drafting', description: 'Comprehensive partnership agreements' },
        { icon: CheckCircle, title: 'LLP Registration', description: 'Limited liability partnership setup' },
        { icon: AlertCircle, title: 'Legal Consultation', description: 'Strategic partnership advice' }
      ]
    },
    'due-diligence': {
      title: 'Due Diligence',
      subtitle: 'Business Due Diligence Services',
      description: 'Comprehensive due diligence investigations for business transactions, acquisitions, and investments.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070',
      benefits: [
        'Comprehensive business investigation',
        'Risk identification and assessment',
        'Financial and legal review',
        'Intellectual property audit',
        'Detailed reporting and analysis'
      ],
      features: [
        { icon: Shield, title: 'Business Investigation', description: 'Thorough company assessment' },
        { icon: FileText, title: 'Legal Review', description: 'Review contracts and obligations' },
        { icon: CheckCircle, title: 'IP Audit', description: 'Intellectual property verification' },
        { icon: AlertCircle, title: 'Risk Assessment', description: 'Identify potential liabilities' }
      ]
    },
    'immigration-law': {
      title: 'Business Immigration Law',
      subtitle: 'Immigration Solutions for Businesses',
      description: 'Professional immigration law services for businesses seeking to hire international talent and expand globally.',
      image: 'https://images.unsplash.com/photo-1436450412740-6b988f486c6b?q=80&w=2070',
      benefits: [
        'Work visa application support',
        'H-1B and L-1 visa processing',
        'Green card sponsorship',
        'Immigration compliance',
        'Global mobility solutions'
      ],
      features: [
        { icon: Shield, title: 'Visa Applications', description: 'Handle various work visa types' },
        { icon: FileText, title: 'Documentation Support', description: 'Prepare required documentation' },
        { icon: CheckCircle, title: 'Compliance Guidance', description: 'Ensure immigration compliance' },
        { icon: AlertCircle, title: 'Case Management', description: 'End-to-end case handling' }
      ]
    },
    'commercial-law': {
      title: 'IT & Commercial Law',
      subtitle: 'Technology and Commercial Legal Services',
      description: 'Specialized legal services for IT companies and commercial transactions, including contract negotiation and compliance.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070',
      benefits: [
        'Technology contract review',
        'Software licensing agreements',
        'SaaS terms and conditions',
        'Commercial transaction support',
        'IT compliance guidance'
      ],
      features: [
        { icon: Shield, title: 'Contract Review', description: 'Technology agreement analysis' },
        { icon: FileText, title: 'Agreement Drafting', description: 'Custom contract preparation' },
        { icon: CheckCircle, title: 'License Management', description: 'Software licensing solutions' },
        { icon: AlertCircle, title: 'Compliance Support', description: 'IT regulatory compliance' }
      ]
    },
    'privacy-law': {
      title: 'Data Protection & Privacy Law',
      subtitle: 'Data Privacy and Protection Compliance',
      description: 'Comprehensive data protection and privacy law services to ensure compliance with GDPR, CCPA, and other regulations.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070',
      benefits: [
        'GDPR and CCPA compliance',
        'Privacy policy development',
        'Data breach response planning',
        'Privacy impact assessments',
        'Regulatory compliance support'
      ],
      features: [
        { icon: Shield, title: 'Compliance Audit', description: 'Assess current privacy practices' },
        { icon: FileText, title: 'Policy Development', description: 'Create privacy policies' },
        { icon: CheckCircle, title: 'Training Programs', description: 'Employee privacy training' },
        { icon: AlertCircle, title: 'Incident Response', description: 'Data breach response planning' }
      ]
    },
    'amazon-brand-registry': {
      title: 'Amazon Brand Registry (US)',
      subtitle: 'Amazon Brand Protection Services',
      description: 'Complete assistance with Amazon Brand Registry enrollment to protect your brand on the world\'s largest marketplace.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070',
      benefits: [
        'Brand Registry enrollment support',
        'Enhanced brand protection on Amazon',
        'Access to powerful brand tools',
        'Counterfeit protection',
        'Improved product listing control'
      ],
      features: [
        { icon: Shield, title: 'Registry Enrollment', description: 'Complete enrollment process' },
        { icon: FileText, title: 'Documentation Prep', description: 'Prepare required documents' },
        { icon: CheckCircle, title: 'Brand Protection', description: 'Set up brand protection tools' },
        { icon: AlertCircle, title: 'Ongoing Support', description: 'Continued Amazon brand support' }
      ]
    },
    'business-formation': {
      title: 'Business Formation Services',
      subtitle: 'Complete Business Entity Formation',
      description: 'Comprehensive business formation services for all entity types, from initial consultation to complete setup.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070',
      benefits: [
        'Entity selection consultation',
        'Complete formation services',
        'State filing and registration',
        'EIN obtainment',
        'Ongoing compliance support'
      ],
      features: [
        { icon: Shield, title: 'Entity Formation', description: 'Form any business entity type' },
        { icon: FileText, title: 'Documentation', description: 'All required business documents' },
        { icon: CheckCircle, title: 'State Registration', description: 'Handle all state filings' },
        { icon: AlertCircle, title: 'Compliance Setup', description: 'Establish compliance framework' }
      ]
    },
    'company-dissolution': {
      title: 'Company Dissolution',
      subtitle: 'Business Dissolution and Closure Services',
      description: 'Professional company dissolution services to properly wind down your business and meet all legal requirements.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      benefits: [
        'Proper business closure',
        'Asset distribution guidance',
        'Tax compliance assistance',
        'Creditor notification',
        'State filing completion'
      ],
      features: [
        { icon: Shield, title: 'Dissolution Planning', description: 'Plan proper business closure' },
        { icon: FileText, title: 'Filing Services', description: 'Complete dissolution filings' },
        { icon: CheckCircle, title: 'Compliance', description: 'Meet all legal requirements' },
        { icon: AlertCircle, title: 'Tax Guidance', description: 'Final tax compliance support' }
      ]
    },
    'issue-shares': {
      title: 'Issue of Shares',
      subtitle: 'Stock Issuance and Equity Management',
      description: 'Professional services for issuing company shares and managing equity distribution to founders, employees, and investors.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Stock certificate preparation',
        'Cap table management',
        'Equity distribution planning',
        'Securities law compliance',
        'Documentation and filing'
      ],
      features: [
        { icon: Shield, title: 'Share Issuance', description: 'Complete stock issuance services' },
        { icon: FileText, title: 'Documentation', description: 'Prepare all required documents' },
        { icon: CheckCircle, title: 'Cap Table Setup', description: 'Establish capitalization table' },
        { icon: AlertCircle, title: 'Compliance', description: 'Securities law compliance' }
      ]
    },
    'transfer-shares': {
      title: 'Transfer of Shares',
      subtitle: 'Stock Transfer and Ownership Changes',
      description: 'Expert handling of stock transfers and ownership changes with proper documentation and regulatory compliance.',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2070',
      benefits: [
        'Stock transfer documentation',
        'Ownership change recording',
        'Regulatory compliance',
        'Cap table updates',
        'Legal requirement fulfillment'
      ],
      features: [
        { icon: Shield, title: 'Transfer Documentation', description: 'Prepare transfer documents' },
        { icon: FileText, title: 'Recording Services', description: 'Record ownership changes' },
        { icon: CheckCircle, title: 'Cap Table Updates', description: 'Update capitalization records' },
        { icon: AlertCircle, title: 'Compliance Check', description: 'Ensure regulatory compliance' }
      ]
    },
    'good-standing': {
      title: 'Certificate of Good Standing',
      subtitle: 'Good Standing Certificate Services',
      description: 'Obtain certificates of good standing from state authorities to verify your business compliance status.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'State certificate obtainment',
        'Compliance verification',
        'Required for business transactions',
        'Expedited processing available',
        'Multiple state support'
      ],
      features: [
        { icon: Shield, title: 'Certificate Request', description: 'Request from state authorities' },
        { icon: FileText, title: 'Documentation', description: 'Handle all required paperwork' },
        { icon: CheckCircle, title: 'Expedited Service', description: 'Fast processing options' },
        { icon: AlertCircle, title: 'Multiple States', description: 'Support for all states' }
      ]
    },
    'name-change': {
      title: 'Company Name Change',
      subtitle: 'Business Name Change Services',
      description: 'Professional assistance with changing your company name, including all necessary filings and notifications.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      benefits: [
        'Complete name change process',
        'State filing and amendments',
        'DBA registration if needed',
        'Legal compliance assurance',
        'Notification assistance'
      ],
      features: [
        { icon: Shield, title: 'Name Change Filing', description: 'File amendments with state' },
        { icon: FileText, title: 'Documentation Update', description: 'Update all business documents' },
        { icon: CheckCircle, title: 'DBA Registration', description: 'Register doing business as name' },
        { icon: AlertCircle, title: 'Compliance', description: 'Ensure legal compliance' }
      ]
    },
    'company-renewals': {
      title: 'Company Renewals',
      subtitle: 'Annual Report and Business Renewal Services',
      description: 'Never miss a deadline with our company renewal and annual report filing services.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Annual report preparation',
        'Deadline monitoring and reminders',
        'Timely filing services',
        'Good standing maintenance',
        'Multi-state support'
      ],
      features: [
        { icon: Shield, title: 'Annual Reports', description: 'Prepare and file annual reports' },
        { icon: FileText, title: 'Deadline Tracking', description: 'Monitor all renewal deadlines' },
        { icon: CheckCircle, title: 'Filing Services', description: 'Handle all state filings' },
        { icon: AlertCircle, title: 'Compliance', description: 'Maintain good standing status' }
      ]
    },
    'nominee-services': {
      title: 'Nominee Director & Secretarial Services',
      subtitle: 'Corporate Governance Support Services',
      description: 'Professional nominee director and company secretarial services to meet corporate governance requirements.',
      image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?q=80&w=2073',
      benefits: [
        'Nominee director services',
        'Company secretarial support',
        'Corporate governance compliance',
        'Meeting management',
        'Record keeping services'
      ],
      features: [
        { icon: Shield, title: 'Nominee Services', description: 'Professional nominee directors' },
        { icon: FileText, title: 'Secretarial Support', description: 'Company secretary services' },
        { icon: CheckCircle, title: 'Compliance Management', description: 'Corporate governance compliance' },
        { icon: AlertCircle, title: 'Record Keeping', description: 'Maintain corporate records' }
      ]
    },
    'bookkeeping': {
      title: 'Bookkeeping',
      subtitle: 'Professional Bookkeeping Services',
      description: 'Accurate and reliable bookkeeping services to keep your financial records organized and compliant.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Accurate financial record keeping',
        'Monthly reconciliations',
        'Financial reporting',
        'Tax preparation support',
        'Cloud-based accounting'
      ],
      features: [
        { icon: Shield, title: 'Record Keeping', description: 'Maintain accurate financial records' },
        { icon: FileText, title: 'Reconciliations', description: 'Monthly account reconciliations' },
        { icon: CheckCircle, title: 'Financial Reports', description: 'Regular financial reporting' },
        { icon: AlertCircle, title: 'Tax Support', description: 'Prepare for tax filing' }
      ]
    },
    'tax-advisory': {
      title: 'Tax Advisory & Filing',
      subtitle: 'Tax Planning and Compliance Services',
      description: 'Expert tax advisory and filing services to minimize tax liability and ensure compliance with all regulations.',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      benefits: [
        'Strategic tax planning',
        'Tax return preparation',
        'Compliance assurance',
        'Tax savings strategies',
        'IRS representation'
      ],
      features: [
        { icon: Shield, title: 'Tax Planning', description: 'Strategic tax planning services' },
        { icon: FileText, title: 'Return Preparation', description: 'Professional tax return filing' },
        { icon: CheckCircle, title: 'Compliance', description: 'Ensure tax compliance' },
        { icon: AlertCircle, title: 'IRS Support', description: 'Representation before IRS' }
      ]
    },
    'cfo-services': {
      title: 'CFO Services',
      subtitle: 'Fractional CFO and Financial Management',
      description: 'Access experienced CFO-level financial expertise without the full-time cost through our fractional CFO services.',
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=2070',
      benefits: [
        'Strategic financial planning',
        'Cash flow management',
        'Financial modeling and forecasting',
        'Fundraising support',
        'Experienced CFO guidance'
      ],
      features: [
        { icon: Shield, title: 'Financial Strategy', description: 'Develop financial strategies' },
        { icon: FileText, title: 'Cash Flow Management', description: 'Optimize cash flow' },
        { icon: CheckCircle, title: 'Forecasting', description: 'Financial modeling and projections' },
        { icon: AlertCircle, title: 'Fundraising', description: 'Support capital raising efforts' }
      ]
    }
  };

  const defaultService = {
    title: 'Our Services',
    subtitle: 'Professional Trademark and Intellectual Property Services',
    description: 'Comprehensive protection for your brand and intellectual property.',
    image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzYzNTAyNjI5fDA&ixlib=rb-4.1.0&q=85',
    benefits: [
      'Expert guidance throughout the process',
      'Comprehensive protection strategies',
      'Dedicated case management',
      'Competitive pricing',
      'High success rates'
    ],
    features: [
      { icon: Shield, title: 'Professional Service', description: 'Expert handling of all trademark matters' },
      { icon: FileText, title: 'Complete Documentation', description: 'All paperwork handled professionally' },
      { icon: CheckCircle, title: 'Success Focus', description: 'Maximizing your chances of approval' },
      { icon: AlertCircle, title: 'Support', description: '24/7 customer support available' }
    ]
  };

  const currentService = serviceData[service] || defaultService;

  return (
    <div className="min-h-screen">
      <SEO 
        title={`${currentService.title} - Expert Services`}
        description={currentService.description}
        keywords={`${currentService.title}, trademark services, intellectual property, IP protection, USPTO filing, brand protection`}
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url(${currentService.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-teal-600/20 backdrop-blur-sm border border-teal-400/30 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-300 text-sm font-semibold">✓ Professional Service</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {currentService.title}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed">
              {currentService.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setShowPopup(true)}
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Get Started Now →
              </Button>
              <Button
                onClick={() => window.location.href = '/contact'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all duration-300"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section with Image */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Service Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {currentService.description}
              </p>
              
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-l-4 border-teal-500 p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Key Benefits</h3>
                <ul className="space-y-3">
                  {currentService.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              <img
                src={currentService.image}
                alt={currentService.title}
                className="rounded-xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 rounded-lg shadow-xl max-w-xs">
                <p className="text-3xl font-bold mb-1">98%</p>
                <p className="text-sm">Client satisfaction rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What's Included
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive services tailored to your needs
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {currentService.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-gray-200 hover:border-teal-400 bg-white">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Icon className="h-8 w-8 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </Card>
              );
            })}
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Process Section - 3 Steps */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Service Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From consultation to completion in three simple steps
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  1
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Initial Review</h3>
                <p className="text-gray-600 leading-relaxed">
                  We review your requirements and conduct a preliminary assessment to ensure the best approach for your case.
                </p>
              </Card>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  2
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Expert Execution</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our experienced team handles all documentation, filing, and communication with relevant authorities on your behalf.
                </p>
              </Card>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  3
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Continuous Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  We provide ongoing assistance, updates, and support to ensure successful completion and long-term protection.
                </p>
              </Card>
            </div>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of working with dedicated professionals
            </p>
          </div>
          
          <ResponsiveCardSlider className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fast Turnaround</h3>
              <p className="text-gray-600 leading-relaxed">
                We understand time is valuable. Our streamlined processes ensure quick and efficient service delivery without compromising quality.
              </p>
            </Card>
            
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Expert Team</h3>
              <p className="text-gray-600 leading-relaxed">
                Our experienced professionals have handled thousands of cases successfully, bringing deep expertise to every project.
              </p>
            </Card>
            
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Personalized Approach</h3>
              <p className="text-gray-600 leading-relaxed">
                Every case is unique. We tailor our services to meet your specific needs and business objectives.
              </p>
            </Card>
            
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Transparent Pricing</h3>
              <p className="text-gray-600 leading-relaxed">
                No hidden fees or surprises. We provide clear, upfront pricing so you know exactly what to expect.
              </p>
            </Card>
            
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">📞</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Direct access to your case manager ensures clear communication and prompt responses to all your questions.
              </p>
            </Card>
            
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                With a 98% client satisfaction rate, our results speak for themselves. Join thousands of happy clients.
              </p>
            </Card>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Pricing Section - Show only for trademark-registration */}
      {service === 'trademark-registration' && (
        <PricingSection onGetStarted={() => setShowPopup(true)} />
      )}

      {/* Common Questions Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Common Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get quick answers to frequently asked questions about our services
            </p>
          </div>
          
          <ResponsiveCardSlider className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-teal-400 transition-all">
              <h3 className="text-lg font-bold text-slate-900 mb-3">How long does the process take?</h3>
              <p className="text-gray-600">
                The timeline varies depending on the specific service, but most applications are filed within 1-3 business days. The overall process typically takes 8-12 months for completion.
              </p>
            </Card>
            
            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-teal-400 transition-all">
              <h3 className="text-lg font-bold text-slate-900 mb-3">What information do I need to provide?</h3>
              <p className="text-gray-600">
                We'll guide you through the required information during our initial consultation. Generally, you'll need details about your mark, business information, and intended use.
              </p>
            </Card>
            
            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-teal-400 transition-all">
              <h3 className="text-lg font-bold text-slate-900 mb-3">What are the costs involved?</h3>
              <p className="text-gray-600">
                Our pricing is transparent and competitive. Costs include our service fee and government filing fees. We'll provide a detailed breakdown before you commit.
              </p>
            </Card>
            
            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-teal-400 transition-all">
              <h3 className="text-lg font-bold text-slate-900 mb-3">Can you guarantee approval?</h3>
              <p className="text-gray-600">
                While we can't guarantee approval, our thorough approach and expertise significantly increase your chances of success. We'll assess viability before filing.
              </p>
            </Card>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?q=80&w=2070"
            alt="Professional team"
            className="w-full h-full object-cover"
          />
          {/* Color Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-800/90 to-slate-900/95"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-6">
            <span className="text-white text-sm font-semibold">✓ Trusted by Thousands of Businesses</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Get Started with {currentService.title}?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Let our experienced team handle your {currentService.title.toLowerCase()} needs. Contact us today for a free consultation and discover how we can help protect and grow your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-7 text-lg font-bold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
            >
              Get Started Now →
            </Button>
            <Button
              onClick={() => window.location.href = '/contact'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-10 py-7 text-lg font-bold transition-all duration-300"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default ServicePage;